import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Settings } from "lucide-react"
import { useEffect, useState } from "react"

import {
  DashboardGrid,
  FinanceAccountsWidget,
  PeopleStatsWidget,
  WidgetCustomizer,
} from "@/components/dashboard"
import { DashboardSkeleton } from "@/components/skeletons/dashboard-skeleton"
import { Button } from "@/components/ui/button"
import { getDashboard } from "@/http/organizations"
import { useDashboard } from "@/store/dashboard"
import { useOrganization } from "@/store/organization"

const dashboardQueryOptions = (orgSlug: string) =>
  queryOptions({
    queryKey: ["dashboard", orgSlug],
    queryFn: async () => {
      const response = await getDashboard(orgSlug)
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data
    },
  })

/**
 * Dashboard route - Phase 4 Implementation
 * Displays customizable widgets with organization statistics
 */
export const Route = createFileRoute("/_authenticated/$orgSlug/dashboard")({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      dashboardQueryOptions(params.orgSlug),
    )
    return {
      title: "Painel",
      path: `/${params.orgSlug}/dashboard`,
    }
  },
  component: Dashboard,
  pendingComponent: DashboardSkeleton,
})

function Dashboard() {
  const { currentOrg } = useOrganization()
  const { enabledWidgets, widgetOrder } = useDashboard()
  const [isCustomizerOpen, setIsCustomizerOpen] = useState(false)
  const path = Route.useLoaderData()
  const { breadcrumbs } = Route.useRouteContext()
  const { orgSlug } = Route.useParams()

  // Fetch dashboard data
  const { data, isLoading } = useSuspenseQuery(dashboardQueryOptions(orgSlug))

  useEffect(() => {
    breadcrumbs.setCurrentPath(path)
  }, [breadcrumbs, path])

  // Widget registry
  const widgets = {
    people: (
      <PeopleStatsWidget
        key="people"
        data={data?.people}
        isLoading={isLoading}
        orgSlug={currentOrg?.slug}
      />
    ),
    finance: (
      <FinanceAccountsWidget
        key="finance"
        data={data?.finance}
        isLoading={isLoading}
        orgSlug={currentOrg?.slug}
      />
    ),
  }

  // Render widgets based on order and enabled state
  const renderedWidgets = widgetOrder
    .filter((widgetId) => enabledWidgets.includes(widgetId))
    .map((widgetId) => widgets[widgetId as keyof typeof widgets])
    .filter(Boolean)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Painel</h1>
          {currentOrg && (
            <p className="text-muted-foreground">{currentOrg.name}</p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsCustomizerOpen(true)}
        >
          <Settings className="mr-2 h-4 w-4" />
          Personalizar
        </Button>
      </div>

      {/* Widgets Grid */}
      {renderedWidgets.length > 0 ? (
        <DashboardGrid>{renderedWidgets}</DashboardGrid>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-muted-foreground mb-4">
            Nenhum widget habilitado. Clique em "Personalizar" para adicionar
            widgets ao seu painel.
          </p>
          <Button onClick={() => setIsCustomizerOpen(true)}>
            <Settings className="mr-2 h-4 w-4" />
            Personalizar painel
          </Button>
        </div>
      )}

      {/* Widget Customizer Dialog */}
      <WidgetCustomizer
        open={isCustomizerOpen}
        onOpenChange={setIsCustomizerOpen}
      />
    </div>
  )
}
